/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import * as moment from 'moment/moment';

import {DATE_FORMAT} from '../../../../../app.constants';
import {MonthView, MonthViewDay} from '../../../../../models/time-and-attendance.interfaces';
import {AbsenceEvent} from '../calendar-absence/calendar-absence.component';
import {CalendarUtils} from '../calendar-utils';
import {CalendarView} from '../calendar-view';

@Component({
  selector: 'gp-calendar-month-view',
  templateUrl: './calendar-month-view.component.html',
  styleUrls: ['./calendar-month-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarMonthViewComponent extends CalendarView implements OnChanges {

  @Input()
  public isReadOnly: boolean = true;

  @Input()
  public isEss: boolean = false;

  @Input()
  public viewDate: Date;

  @Input()
  public workcycle: any[] = [];

  @Input()
  public absences: Array<any> = [];

  @Input()
  public presences: Array<any> = [];

  @Input()
  public eventTypes: any;

  @Input()
  public eventTypesByFamilyTransId: { string: Array<any> };

  @Output('eventClicked')
  public onClickEvent: EventEmitter<any> = new EventEmitter();

  @Output('eventChanged')
  public eventChanged: EventEmitter<any> = new EventEmitter();

  public monthView: MonthView;
  public menuDate: Date;
  public menuRowIndex: number;

  constructor(protected translate: TranslateService) {
    super(translate);
    this.translate.onLangChange.subscribe(() => {
      this.loadView();
    });
  }

  public ngOnChanges() {
    if (this.viewDate) {
      this.loadView();
    }
  }

  // TODO: refactor calendar month view and calendar week view
  // as there is a lot of code duplication
  public loadView() {
    // create a month object from a date
    this.monthView = CalendarUtils.getMonthView(this.viewDate);
    // bind all needed datas to monthView.days :
    // - color and labels from workcycle
    // - absences from absences
    // - presences from presences
    // - then check priority
    this.monthView.days.forEach(day => {
      const colorAndLabel = super.getDayColorAndLabel(this.workcycle, moment(day.date).format(DATE_FORMAT));
      day.backgroundColor = colorAndLabel.color;
      day.label = colorAndLabel.label;
      day.type = colorAndLabel.type;
      day.textColor = colorAndLabel.textColor;
      day.absences = this.getDayAbsences(day.date);
      day.presences = this.getDayPresences(day.date);
      day.hasPriorityOnAbsence = [];

      if (day.absences && day.absences.length) {
        day.absences.forEach((absence, index) => {
          this.checkPriority(day, absence, index);
        });
      }

    });
  }

  // TODO: type absence / pendingAbsence
  // nb : In Time and attendance component, these 2 lists/types are merged in one
  // and a view object is added (cf getEventViewOpts)
  public checkPriority(day: MonthViewDay, absence: any, index: number) {
    if (this.hasDayTypePriorityOnAbsence(day, absence)) {
      day.cssClass = 'has-day-type-priority-on-absence';
      day.hasPriorityOnAbsence[index] = true;
    } else {
      day.hasPriorityOnAbsence[index] = false;
    }
  }

  public onEventChanged(event: any) {
    this.loadView();
    this.eventChanged.emit(event);
  }

  public addMenuOpts(date: Date, rowIndex: number) {
    this.menuDate = date;
    this.menuRowIndex = rowIndex;
  }

  public hideMenu(event: any) {
    if (!event.target.closest('.add-dropdown-btn, .dropdown-menu, gp-dropdown-select')) {
      this.menuDate = undefined;
      this.menuRowIndex = undefined;
    }
  }

  public onEditEventClick(event: any, e: any) {
    const btn = e.target.closest('.icon-btn');
    if (!btn) {
      return;
    }
    const action = btn.getAttribute('data-action');
    this.onClickEvent.emit({event, action});
  }

  public onEventResize(data: { event: AbsenceEvent, action: string }) {
    this.onClickEvent.emit(data);
  }

  public onAddEventClick(date: Date) {
    const event = {
      type: undefined,
      isNew: true,
      attributes: {
        begin: moment(date).format(DATE_FORMAT),
        end: moment(date).format(DATE_FORMAT),
        type: undefined,
        beginDayfactor: 0,
        endDayfactor: 0,
        transid: undefined,
        unitTransId: undefined,
        dirty: true,
        allowedDurationType: undefined
      }
    };

    this.onClickEvent.emit({event, action: 'add'});

    this.menuDate = undefined;
    this.menuRowIndex = undefined;
  }


  private hasDayTypePriorityOnAbsence(day: MonthViewDay, absence: any): boolean {
    const eventForAbsence = this.eventTypes.find(event => event.name === absence.attributes.name);
    const absencePriority = (eventForAbsence && eventForAbsence.priority) ? eventForAbsence.priority : 0;

    const eventForDay = this.eventTypes.find(event => event.name === day.type);
    const dayPriority = (eventForDay && eventForDay.priority) ? eventForDay.priority : 0;

    return dayPriority > absencePriority;
  }

  private getDayAbsences(date: Date) {
    return this.absences
      .filter(event => moment(date).isBetween(event.attributes.begin, event.attributes.end, null, '[]'));
  }

  private getDayPresences(date: Date) {
    return this.presences
      .filter(event => moment(date).isBetween(event.attributes.begindate, event.attributes.enddate, null, '[]'));
  }
}
